import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { useAuth } from "../../context/auth-context";
import axiosInstance from "../../utils/axiosConfig";
import { apiQueryKey } from "./queryKeys";

type BanksApiBody = {
  data?: Array<{
    code?: string | number;
    name?: string;
    bankCode?: string | number;
    bankName?: string;
  }>;
};

const emptyMap = new Map<string, string>();

function codeCandidates(code: unknown): string[] {
  if (code === null || code === undefined) return [];
  const raw = String(code).trim();
  if (!raw) return [];
  const numNorm = /^\d+$/.test(raw) ? String(Number(raw)) : raw;
  const out = [raw, numNorm, raw.padStart(3, "0"), numNorm.padStart(3, "0")];
  return Array.from(new Set(out));
}

/**
 * Master bank list from both GET api/banks/palmpay and GET api/banks.
 * Normalizes code variants so old/new provider codes resolve reliably.
 */
export function useBanksLookupQuery(enabledParam = true) {
  const { isAuthenticated } = useAuth();

  return useQuery<Map<string, string>, AxiosError>({
    queryKey: apiQueryKey("banks-lookup"),
    enabled: Boolean(isAuthenticated) && enabledParam,
    staleTime: 60 * 60 * 1000,
    queryFn: async ({ signal }) => {
      const [palmpayRes, legacyRes] = await Promise.all([
        axiosInstance.get<BanksApiBody>("api/banks/palmpay", { signal }),
        axiosInstance.get<BanksApiBody>("api/banks", { signal }),
      ]);
      const rows = [
        ...(Array.isArray(palmpayRes.data?.data) ? palmpayRes.data.data : []),
        ...(Array.isArray(legacyRes.data?.data) ? legacyRes.data.data : []),
      ];
      const m = new Map<string, string>();
      for (const row of rows) {
        const name =
          (typeof row?.bankName === "string" && row.bankName.trim()) ||
          (typeof row?.name === "string" && row.name.trim()) ||
          "";
        const codes = [
          ...codeCandidates(row?.bankCode),
          ...codeCandidates(row?.code),
        ];
        for (const c of codes) {
          if (!m.has(c)) m.set(c, name || c);
        }
      }
      return m;
    },
  });
}

/** Stable empty map when the query has not resolved yet. */
export function banksLookupMap(
  data: Map<string, string> | undefined
): Map<string, string> {
  return data ?? emptyMap;
}
